import { lazy, ReactElement, Suspense } from 'react';
import { Navigate, Route, Routes as RouterRoutes } from 'react-router-dom';
import { Divider, Result } from 'antd';
import { bulkTargetingRoutes } from '@app/routes/bulk.targeting.routes';
import { EAppRoutes, Permission } from '@common/enums';
import { AuthContextProvider } from '@common/hooks/auth/AuthContextProvider';
import FullPageSpinnerLayout from '@common/layouts/FullPageSpinnerLayout';
import NavLayout from '@common/layouts/HeaderNavLayout';
import { AuthAndPermissionGuard } from '@features/auth/components/AuthAndPermissionGuard';
import AuthenticatedRoutes from '@features/auth/components/AuthenticatedRoutes';
import { BulkTargetingRouteWithContext } from '@features/bulk-edit/contexts';
import { CreativeIdParamGuard } from '@features/creatives/creatives-builder/components/common/CreativeIdParamGuard';
import AccountSelection from '@features/navigation/components/AccountSelection';
import { AccountContextProvider } from '@features/navigation/hooks/useAccountContextProvider';
import { TargetingRouteWithContext } from '@features/targeting/contexts';
import { creativesByIdRoutes, creativesPublicRoutes, creativesRoutes, targetingRoutes } from './routes';

const UserLogoutLazy = lazy(() => import('@features/auth/components/UserLogout'));
const DashboardLazy = lazy(() => import('@pages/Home'));
const AdminUsersInviteLazy = lazy(() => import('@pages/AdminUsersInvite'));
const LoginLazy = lazy(() => import('@pages/Login'));
const CampaignManagementLazy = lazy(() => import('@pages/CampaignManagement'));
const CreateCampaignLazy = lazy(() => import('@pages/CampaignManagement/Campaign/CreateCampaign'));
const UpdateCampaignLazy = lazy(() => import('@pages/CampaignManagement/Campaign/UpdateCampaign'));
const BulkEditCampaignLazy = lazy(() => import('@pages/CampaignManagement/Campaign/BulkEditCampaign'));
const CreateStrategyLazy = lazy(() => import('@pages/CampaignManagement/Strategy/CreateStrategy'));
const EditStrategyLazy = lazy(() => import('@pages/CampaignManagement/Strategy/EditStrategy'));
const BulkEditStrategyLazy = lazy(() => import('@pages/CampaignManagement/Strategy/BulkEditStrategy'));
const BulkEditStrategyOptimizationLazy = lazy(
  () => import('@pages/CampaignManagement/Strategy/BulkEditStrategyOptimization')
);
const CreateStrategyOptimizationLazy = lazy(
  () => import('@pages/CampaignManagement/Strategy/CreateStrategyOptimization')
);
const EditStrategyOptimizationLazy = lazy(() => import('@pages/CampaignManagement/Strategy/EditStrategyOptimization'));
const ViewCampaignLazy = lazy(() => import('@pages/CampaignManagement/View'));
const CreativesLazy = lazy(() => import('@pages/Creatives'));
const ReportingLazy = lazy(() => import('@pages/Reporting'));
const PlanningLazy = lazy(() => import('@pages/Planning'));
const AdaptCtvTemplatesLazy = lazy(() => import('@pages/AdaptCtvTemplates'));
const CreateAdaptCtvTemplateLazy = lazy(() => import('@pages/AdaptCtvTemplates/CreateAdaptCtvTemplate'));
const EditAdaptCtvTemplateByIdLazy = lazy(() => import('@pages/AdaptCtvTemplates/EditAdaptCtvTemplateById'));

const headerActions = (
  <>
    <AccountSelection />
    <Divider type='vertical' style={{ margin: '0 24px', height: 32 }} />
  </>
);

const AppRoutes = (): ReactElement => {
  return (
    <Suspense fallback={<FullPageSpinnerLayout />}>
      <AuthContextProvider>
        <AccountContextProvider>
          <RouterRoutes>
            <Route element={<CreativeIdParamGuard />}>{creativesPublicRoutes}</Route>

            <Route element={<AuthenticatedRoutes />}>
              <Route path={EAppRoutes.Login} element={<LoginLazy />} />
              <Route path={'*'} element={<Result status='404' title='This section in progress' />} />
              <Route element={<NavLayout />}>
                <Route path={EAppRoutes.Logout} element={<UserLogoutLazy />} />
                <Route path={EAppRoutes.Home} element={<DashboardLazy />} />
                <Route path={EAppRoutes.AdminUsersInvite} element={<AdminUsersInviteLazy />} />
                <Route path={EAppRoutes.Reporting} element={<ReportingLazy />} />
                <Route path={EAppRoutes.Planning} element={<PlanningLazy />} />
                <Route path={EAppRoutes.Reporting} element={<ReportingLazy />} />
              </Route>
              <Route element={<NavLayout headerActions={headerActions} />}>
                <Route path={EAppRoutes.CampaignManagement} element={<CampaignManagementLazy />} />
                <Route element={<AuthAndPermissionGuard permission={Permission.AccessCreativeModule} />}>
                  <Route path={EAppRoutes.Creatives} element={<CreativesLazy />} />
                </Route>
                <Route element={<AuthAndPermissionGuard permission={Permission.InternalUser} />}>
                  <Route path={EAppRoutes.AdaptCtvTemplates} element={<AdaptCtvTemplatesLazy />} />
                </Route>
              </Route>
              <Route element={<AuthAndPermissionGuard permission={Permission.AccessCreativeModule} />}>
                <Route element={<CreativeIdParamGuard />}>{creativesByIdRoutes}</Route>
                {creativesRoutes}
              </Route>

              <Route element={<TargetingRouteWithContext />}>
                <Route path={EAppRoutes.CreateStrategyOptimization} element={<CreateStrategyOptimizationLazy />}>
                  {targetingRoutes}
                </Route>

                <Route path={EAppRoutes.EditStrategyOptimization} element={<EditStrategyOptimizationLazy />}>
                  {targetingRoutes}
                </Route>
              </Route>

              <Route path={EAppRoutes.CreateCampaign} element={<CreateCampaignLazy />} />
              <Route path={EAppRoutes.EditCampaign} element={<UpdateCampaignLazy />} />
              <Route path={EAppRoutes.BulkEditCampaign} element={<BulkEditCampaignLazy />} />
              <Route path={EAppRoutes.CreateStrategy} element={<CreateStrategyLazy />} />
              <Route path={EAppRoutes.EditStrategy} element={<EditStrategyLazy />} />

              <Route element={<AuthAndPermissionGuard permission={Permission.InternalUser} />}>
                <Route path={EAppRoutes.CreateAdaptCtvTemplate} element={<CreateAdaptCtvTemplateLazy />} />
                <Route path={EAppRoutes.EditAdaptCtvTemplateById} element={<EditAdaptCtvTemplateByIdLazy />} />
              </Route>

              <Route element={<BulkTargetingRouteWithContext />}>
                <Route path={EAppRoutes.BulkEditStrategy} element={<BulkEditStrategyLazy />} />
                <Route path={EAppRoutes.BulkEditStrategyOptimization} element={<BulkEditStrategyOptimizationLazy />}>
                  {bulkTargetingRoutes}
                </Route>
              </Route>

              <Route path={EAppRoutes.CreateStrategy} element={<CreateStrategyLazy />} />
              <Route path={EAppRoutes.ViewCampaign} element={<ViewCampaignLazy />} />
              <Route path='*' element={<Navigate to={EAppRoutes.Reporting} />} />
            </Route>
          </RouterRoutes>
        </AccountContextProvider>
      </AuthContextProvider>
    </Suspense>
  );
};

export default AppRoutes;
